/**
 * mobile-pwa Phase 5.2: IndexedDB 直近 10-20 entry の LRU cache。
 *
 * scenario / scene fetch の write-through cache を提供する。 online で fetch 成功時に
 * persist し、 offline / fetch 失敗時に fallback として利用する。 cache は 1 store
 * `entries` に namespace + key 複合キーで格納、 `lastAccessedAt` を持ち LRU eviction
 * で上限 20 entry を維持する。
 *
 * IndexedDB は SSR / 一部 private mode で利用不可のため、 graceful fallback として
 * 失敗時は in-memory no-op に degrade する (= UX 破壊しない方針、 reader-style-preset
 * 経路 と同じ doctrine)。
 *
 * cache scope:
 * - `scene:{playlineId}` → 現在 scene snapshot (= useCurrentScene)
 * - `scenario:{scenarioId}` → scenario metadata (= useScenario)
 *
 * eviction policy: count >= LRU_MAX_ENTRIES のときに lastAccessedAt 最古の entry を
 * 削除して空き枠を確保。 namespace 横断で同 store 内 LRU。
 */

const DB_NAME = "palimia-client-cache";
// v2: portraitUrl を含むシナリオスキーマ追加に伴い旧キャッシュを強制クリア。
const DB_VERSION = 2;
const STORE_NAME = "entries";
const LRU_MAX_ENTRIES = 20;
// LRU index name (= lastAccessedAt ascending order で削除候補抽出に使う)。
const INDEX_LAST_ACCESSED_AT = "lastAccessedAt";

export interface CacheEntry<T> {
  /** 複合 key (= `${namespace}:${id}`、 store の primary key)。 */
  key: string;
  /** namespace 識別子 (= "scene" / "scenario")。 invalidate 用途で保持。 */
  namespace: string;
  /** 実体 (= JSON serializable な scene / scenario 等)。 */
  value: T;
  /** 書込み時刻 (= 単調増加 ms epoch)、 freshness 判定の参考に保持。 */
  storedAt: number;
  /** 直近 access 時刻 (= LRU eviction 基準)、 read 時にも更新される。 */
  lastAccessedAt: number;
}

export interface IndexedDBCache {
  /** namespace + id を渡し、 存在するなら entry を返す。 access 時 lastAccessedAt 更新。 */
  get<T>(namespace: string, id: string): Promise<CacheEntry<T> | null>;
  /** namespace + id + value を保存。 既存 entry の上書き OK。 */
  set<T>(namespace: string, id: string, value: T): Promise<void>;
  /** namespace + id を消す。 不在は no-op。 */
  delete(namespace: string, id: string): Promise<void>;
  /** store 全 clear。 sign-out 等で利用想定 (= 本 PR 内では未利用)。 */
  clear(): Promise<void>;
}

function isIndexedDBAvailable(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.indexedDB === "undefined") return false;
  return true;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      // バージョンアップ時は旧 store を削除して再作成することでキャッシュを強制クリアする。
      // これにより portraitUrl 等スキーマ変更後も古いデータが返らない。
      if (event.oldVersion > 0 && db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }
      const store = db.createObjectStore(STORE_NAME, { keyPath: "key" });
      store.createIndex(INDEX_LAST_ACCESSED_AT, "lastAccessedAt", {
        unique: false,
      });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
  });
}

function compositeKey(namespace: string, id: string): string {
  return `${namespace}:${id}`;
}

function wrapRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB op failed"));
  });
}

async function evictIfNeeded(store: IDBObjectStore): Promise<void> {
  const countReq = store.count();
  const count = await wrapRequest(countReq);
  if (count < LRU_MAX_ENTRIES) return;

  // lastAccessedAt index で最古 entry を 1 件取り出して delete。
  const index = store.index(INDEX_LAST_ACCESSED_AT);
  const cursorReq = index.openCursor();
  await new Promise<void>((resolve, reject) => {
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor === null) {
        resolve();
        return;
      }
      const deleteReq = store.delete(cursor.primaryKey);
      deleteReq.onsuccess = () => resolve();
      deleteReq.onerror = () =>
        reject(deleteReq.error ?? new Error("indexedDB delete failed"));
    };
    cursorReq.onerror = () =>
      reject(cursorReq.error ?? new Error("indexedDB cursor failed"));
  });
}

class RealIndexedDBCache implements IndexedDBCache {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private async getDB(): Promise<IDBDatabase> {
    if (this.dbPromise === null) {
      this.dbPromise = openDB().catch((err) => {
        // 開けない (= quota / private mode) なら次回再試行を許す。
        this.dbPromise = null;
        throw err;
      });
    }
    return this.dbPromise;
  }

  async get<T>(
    namespace: string,
    id: string,
  ): Promise<CacheEntry<T> | null> {
    const db = await this.getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const key = compositeKey(namespace, id);
    const entry = (await wrapRequest(store.get(key))) as
      | CacheEntry<T>
      | undefined;
    if (entry === undefined) return null;
    // read 時に lastAccessedAt 更新 (= LRU promotion)。 await せず fire-and-forget で
    // も良いが、 transaction 整合性のため同 tx 内で put。
    entry.lastAccessedAt = Date.now();
    await wrapRequest(store.put(entry));
    return entry;
  }

  async set<T>(namespace: string, id: string, value: T): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await evictIfNeeded(store);
    const now = Date.now();
    const entry: CacheEntry<T> = {
      key: compositeKey(namespace, id),
      namespace,
      value,
      storedAt: now,
      lastAccessedAt: now,
    };
    await wrapRequest(store.put(entry));
  }

  async delete(namespace: string, id: string): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await wrapRequest(store.delete(compositeKey(namespace, id)));
  }

  async clear(): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await wrapRequest(store.clear());
  }
}

class NoopIndexedDBCache implements IndexedDBCache {
  async get<T>(): Promise<CacheEntry<T> | null> {
    return null;
  }
  async set<T>(): Promise<void> {
    return;
  }
  async delete(): Promise<void> {
    return;
  }
  async clear(): Promise<void> {
    return;
  }
}

let singleton: IndexedDBCache | null = null;

/**
 * Singleton cache を返す。 IndexedDB 利用不可な環境 (= SSR / private mode) では
 * NoopIndexedDBCache を返し、 caller 側で分岐不要にする。 ただし IDBDatabase の
 * open 失敗 (= quota / 権限) は initial get/set 時にも起きるため、 caller は
 * try/catch で error を拾って silently degrade すべき。
 */
export function getIndexedDBCache(): IndexedDBCache {
  if (singleton !== null) return singleton;
  singleton = isIndexedDBAvailable()
    ? new RealIndexedDBCache()
    : new NoopIndexedDBCache();
  return singleton;
}

// test 用 reset (= 本 module を import した直後の状態に戻す)。
export function __resetIndexedDBCacheForTest(): void {
  singleton = null;
}

export { LRU_MAX_ENTRIES, DB_NAME, STORE_NAME };
